import { Navigate } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { getUserRoleFlags } from '@/components/layout/navigationSections'
import { useDedicatedOnlyAffiliate } from '@/hooks/useDedicatedOnlyAffiliate'
import { isStagingEnv } from '@/utils/featureFlags'

/**
 * #650 — route guard for pages hidden before the production cut.
 *
 * Renders the page only for admins (who keep access to QA). Everyone else is
 * redirected to the dashboard so a directly-pasted URL can't reach a hidden
 * page. The nav items are hidden separately in navigationSections.ts. Restore
 * tracked in #654.
 */
export function DeferredRoute({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const { isAdmin } = getUserRoleFlags(user)
  if (isAdmin) return <>{children}</>
  return <Navigate to="/dashboard" replace />
}

/**
 * #669 — route guard for the partner-facing /contracts page, hidden on
 * staging for partner-facing users. Mirrors {@link DeferredRoute} but is
 * gated on the staging host (not the deferred-pages flag). Admins are
 * exempted so the admin contracts surface (/admin/contracts 'All contracts'
 * tab) can reach the contract detail it links to — #669's goal was hiding
 * the partner flow, not admin QA. Production (non-staging host) renders the
 * page unchanged for everyone.
 */
export function StagingHiddenRoute({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const { isAdmin } = getUserRoleFlags(user)
  if (isStagingEnv() && !isAdmin) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

/**
 * #1210 — route guard for /discovery: users whose only affiliate access is
 * Dedicated Affiliates are redirected to the dashboard (the sidebar item is
 * hidden separately via navigationSections.ts).
 *
 * Real-admin bypass: impersonation is admin-only, so `isImpersonating`
 * counts as admin (Header.tsx precedent) — the nav still shows the faithful
 * preview (Discovery hidden), but the route stays reachable so an admin can
 * QA the server-side listing exclusion. While the dedicated check is
 * unresolved we render nothing rather than redirect, so a legitimate
 * non-dedicated affiliate deep-linking here never sees a redirect flicker;
 * guarding outside DiscoveryPage also means its lazy chunk is never
 * downloaded when the redirect fires.
 */
export function DedicatedAffiliateGuard({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const { isImpersonating } = useImpersonation()
  const { hideDiscovery, isResolved } = useDedicatedOnlyAffiliate()
  const realIsAdmin = isImpersonating || getUserRoleFlags(user).isAdmin
  if (realIsAdmin) return <>{children}</>
  if (!isResolved) return null
  if (hideDiscovery) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}
